<script setup>
import { ref, onMounted } from 'vue'
import { handleOAuthCallback } from '@/services/oauth'
import { NSpin } from 'naive-ui'

const status = ref('processing') // 'processing', 'success', 'error'
const message = ref('Processing authentication, please wait...')
const errorDetails = ref('')
const debugInfo = ref('') // Add this for debugging

onMounted(async () => {
  try {
    // For Apple OAuth debugging, capture raw data from the page
    const pathParts = window.location.pathname.split('/');
    const oauthIndex = pathParts.findIndex(part => part === 'oauth');
    const provider = oauthIndex >= 0 && oauthIndex + 1 < pathParts.length
      ? pathParts[oauthIndex + 1]
      : null;
    
    // For Apple, check if we can see raw data before processing
    if (provider === 'apple') {
      try {
        debugInfo.value = `Provider: ${provider}\n`;
        debugInfo.value += `URL: ${window.location.href}\n`;
        
        // Capture any form data if present
        const formElements = document.querySelectorAll('input');
        if (formElements.length > 0) {
          debugInfo.value += "Form elements found:\n";
          formElements.forEach(el => {
            debugInfo.value += `${el.name || 'unnamed'}: ${el.value || 'no value'}\n`;
          });
        } else {
          debugInfo.value += "No form elements found\n";
        }
        
        // Capture body content
        if (document.body) {
          debugInfo.value += `Body text (first 200 chars): ${(document.body.innerText || '').substring(0, 200)}\n`;
        }
        
      } catch (e) {
        console.error('Error in debug capture:', e);
      }
    }
    
    // Process the OAuth callback
    const result = await handleOAuthCallback()
    
    // If handleOAuthCallback doesn't throw an error, update status
    status.value = 'success'
    message.value = 'Authentication successful!'
    
    // Close the window after a short delay
    setTimeout(() => {
      if (window.opener) {
        window.close()
      }
    }, 1000)
  } catch (error) {
    // Handle any errors
    status.value = 'error'
    message.value = 'Authentication failed'
    errorDetails.value = error.message || 'An unknown error occurred'
    console.error('OAuth callback error:', error)
    
    // Close with error after a delay
    setTimeout(() => {
      if (window.opener) {
        window.close()
      }
    }, 3000)
  }
})

// Attempt to force close window if it doesn't close automatically
const forceClose = () => {
  window.close()
}

// Toggle debug info visibility
const showDebug = ref(false)
const toggleDebug = () => {
  showDebug.value = !showDebug.value
}
</script>

<template>
  <div class="oauth-callback">
    <NSpin size="large" :show="status === 'processing'" />
    
    <div class="message-container" :class="status">
      <h2>{{ message }}</h2>
      
      <p v-if="status === 'processing'">
        Communicating with the authentication provider...
      </p>
      
      <p v-if="status === 'success'">
        Authentication successful! You can close this window.
      </p>
      
      <p v-if="status === 'error'" class="error-details">
        {{ errorDetails }}
      </p>
      
      <!-- Show debug info button only in error state -->
      <button v-if="status === 'error' && debugInfo" 
              @click="toggleDebug" 
              class="debug-button">
        {{ showDebug ? 'Hide' : 'Show' }} Debug Info
      </button>
      
      <!-- Debug info panel -->
      <div v-if="showDebug && debugInfo" class="debug-panel">
        <pre>{{ debugInfo }}</pre>
      </div>
      
      <button v-if="status !== 'processing'" @click="forceClose" class="close-button">
        Close Window
      </button>
    </div>
    
    <!-- Hidden form for Apple POST data to be accessible via DOM -->
    <form id="apple-oauth-form" style="display:none;">
      <input type="hidden" id="apple-code" name="code" />
      <input type="hidden" id="apple-state" name="state" />
      <input type="hidden" id="apple-error" name="error" />
    </form>
  </div>
</template>

<style scoped>
.oauth-callback {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  text-align: center;
  padding: 20px;
  background-color: #f8f9fa;
}

.message-container {
  margin-top: 24px;
  max-width: 400px;
}

.message-container h2 {
  font-size: 24px;
  margin-bottom: 12px;
  color: #667eea;
}

.message-container p {
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
}

.message-container.success h2 {
  color: #10b981;
}

.message-container.error h2 {
  color: #ef4444;
}

.error-details {
  background-color: #fee2e2;
  padding: 12px;
  border-radius: 6px;
  color: #b91c1c;
  word-break: break-word;
}

.close-button {
  margin-top: 16px;
  padding: 8px 16px;
  background-color: #667eea;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: #5a67d8;
}

.debug-button {
  margin-top: 12px;
  padding: 6px 12px;
  background-color: #64748b;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.debug-panel {
  margin-top: 12px;
  padding: 12px;
  background-color: #1e293b;
  color: #e2e8f0;
  border-radius: 6px;
  text-align: left;
  max-height: 200px;
  overflow-y: auto;
  width: 100%;
}

.debug-panel pre {
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 12px;
  margin: 0;
}
</style>